// client/src/components/marketer/MarketerProjectCPSCharge.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  readCPSProjectList,
  cacheCPSProjectList,
  readCPSDetail,
  cacheCPSDetail,
  invalidateCPSDetail
} from '../../utils/marketerCPSCache';
import ProjectDetailModal from './project/modals/ProjectDetailModal';
import SettleModal from './cps/modals/SettleModal';
import SettlementHistoryModal from './cps/modals/SettlementHistoryModal';

// ✅ 이미지 프리로드 함수 (브라우저 캐시 활용)
const preloadImage = (url) => {
  if (!url) return;
  const img = new Image();
  img.src = url;
};

// ✅ 결산 기간 상태 체크 (성능 최적화: 한 번의 Date 객체 생성으로 모든 체크)
const getSettlementPeriodStatus = (scheduledDate, status) => {
  if (!scheduledDate) return 'unknown';
  if (status === 'completed') return 'completed';
  if (status === 'auto_reserved') return 'auto_reserved';

  // ✅ 날짜 비교 시 시간 부분 제거 (00:00:00 기준으로 비교)
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const scheduled = new Date(scheduledDate);
  scheduled.setHours(0, 0, 0, 0);

  const sevenDaysAfter = new Date(scheduled.getTime() + 7 * 24 * 60 * 60 * 1000);

  if (now < scheduled) return 'before'; // 결산일 이전
  if (now >= sevenDaysAfter) return 'expired'; // 8일차부터 (결산일+7일 이상)
  return 'active'; // 결산 가능 기간 (결산일 ~ 결산일+6일)
};

function MarketerProjectCPSCharge() {
  const location = useLocation();
  const [uid, setUid] = useState(window.__ME__?.id);

  useEffect(() => {
    const currentUid = window.__ME__?.id;
    if (currentUid !== uid) {
      setUid(currentUid);
    }

    const handleUserReady = (event) => {
      const userId = event.detail?.id;
      if (userId) {
        setUid(userId);
      }
    };

    if (!window.__USER_READY_LISTENERS__) {
      window.__USER_READY_LISTENERS__ = [];
    }
    window.__USER_READY_LISTENERS__.push(handleUserReady);

    return () => {
      if (window.__USER_READY_LISTENERS__) {
        const idx = window.__USER_READY_LISTENERS__.indexOf(handleUserReady);
        if (idx > -1) {
          window.__USER_READY_LISTENERS__.splice(idx, 1);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialProjects = useMemo(() => {
    return uid ? (readCPSProjectList(uid, 'all') || []) : [];
  }, [uid]);

  const hasProjectCache = uid && initialProjects.length > 0;

  const [projects, setProjects] = useState(initialProjects);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [loading, setLoading] = useState(!hasProjectCache);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'in_progress'

  const [currentSettlement, setCurrentSettlement] = useState(null);
  const [allSettlements, setAllSettlements] = useState([]);
  const [selectedRound, setSelectedRound] = useState(1);
  const [settlementLoading, setSettlementLoading] = useState(false);

  const [customerCounts, setCustomerCounts] = useState({}); // { influencerId: count }

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const prevProjectIdRef = useRef(null);
  const prefetchingRef = useRef(new Set());

  const currentProject = projects[currentProjectIndex] || null;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetProjectId = params.get('projectId');

    if (targetProjectId && projects.length > 0 && !loading) {
      const targetIndex = projects.findIndex(p => p.id === targetProjectId);
      if (targetIndex !== -1 && targetIndex !== currentProjectIndex) {
        setCurrentProjectIndex(targetIndex);
        const newParams = new URLSearchParams(location.search);
        newParams.delete('projectId');
        window.history.replaceState({}, '', `${location.pathname}?${newParams.toString()}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, location.search, loading]);

  const fetchSettlementDetail = useCallback(async (projectId, roundNumber, shouldResetInputs = true, forceRefresh = false) => {
    if (!projectId || !roundNumber) return;

    setSettlementLoading(true);

    // ✅ forceRefresh가 true면 캐시 체크를 건너뛰고 무조건 서버에서 최신 데이터 가져옴
    if (!forceRefresh) {
      const cached = readCPSDetail(projectId, roundNumber);
      if (cached) {
        setCurrentSettlement(cached.currentSettlement);
        setAllSettlements(cached.settlements || []);
        if (shouldResetInputs) {
          setCustomerCounts({});
        }
        setSettlementLoading(false);
        return;
      }
    }

    try {
      const res = await fetch(
        `/api/marketer/cps-settlements/${projectId}?roundNumber=${roundNumber}`,
        { credentials: 'include' }
      );
      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.message || '결산 상세 조회 실패');
      }

      setCurrentSettlement(data.currentSettlement);
      setAllSettlements(data.settlements || []);
      if (shouldResetInputs) {
        setCustomerCounts({});
      }

      cacheCPSDetail(projectId, roundNumber, data);

    } catch (e) {
      console.error('결산 상세 조회 오류:', e);
      setError(e.message);
      setCurrentSettlement(null);
    } finally {
      setSettlementLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async (filterType = 'all') => {
    if (!uid) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/marketer/cps-settlements?filter=${filterType}`, {
        credentials: 'include'
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.message || '프로젝트 목록 조회 실패');
      }

      const fetchedProjects = data.projects || [];
      setProjects(fetchedProjects);

      cacheCPSProjectList(uid, fetchedProjects, filterType);

      if (fetchedProjects.length > 0 && fetchedProjects[0].currentRound) {
        fetchSettlementDetail(fetchedProjects[0].id, fetchedProjects[0].currentRound);
      }

    } catch (e) {
      console.error('프로젝트 목록 조회 오류:', e);
      setError(e.message);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [uid, fetchSettlementDetail]);

  useEffect(() => {
    if (uid && !hasProjectCache) {
      fetchProjects('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  useEffect(() => {
    if (currentProject && currentProject.currentRound) {
      const projectId = currentProject.id;
      const targetRound = currentProject.currentRound;

      if (prevProjectIdRef.current !== projectId) {
        prevProjectIdRef.current = projectId;
        setSelectedRound(targetRound);
        setCustomerCounts({});
        setShowSettleModal(false);
        setShowHistoryModal(false);
        fetchSettlementDetail(projectId, targetRound, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectIndex, fetchSettlementDetail]);

  useEffect(() => {
    if (currentProjectIndex < projects.length - 1) {
      const nextProject = projects[currentProjectIndex + 1];
      if (nextProject?.coverThumb) {
        preloadImage(nextProject.coverThumb);
      }
    }
  }, [currentProjectIndex, projects]);

  const handlePrevProject = useCallback(() => {
    if (currentProjectIndex > 0) {
      setCurrentProjectIndex(currentProjectIndex - 1);
    }
  }, [currentProjectIndex]);

  const handleNextProject = useCallback(() => {
    if (currentProjectIndex < projects.length - 1) {
      setCurrentProjectIndex(currentProjectIndex + 1);
    }
  }, [currentProjectIndex, projects.length]);

  const handleFilterChange = useCallback((newFilter) => {
    setFilter(newFilter);
    setCurrentProjectIndex(0);

    const cached = readCPSProjectList(uid, newFilter);
    if (cached) {
      setProjects(cached);
      return;
    }

    fetchProjects(newFilter);
  }, [uid, fetchProjects]);

  const handleRoundChange = useCallback((roundNumber) => {
    if (roundNumber === selectedRound) return;
    setSelectedRound(roundNumber);
    fetchSettlementDetail(currentProject.id, roundNumber);
  }, [selectedRound, currentProject, fetchSettlementDetail]);

  useEffect(() => {
    if (!currentProject?.id || !allSettlements.length || !selectedRound) return;

    const currentIndex = allSettlements.findIndex(s => s.roundNumber === selectedRound);
    if (currentIndex === -1) return;

    const prefetchRound = (round) => {
      const key = `${currentProject.id}-${round.roundNumber}`;

      if (prefetchingRef.current.has(key)) {
        return;
      }

      if (round.status === 'completed') {
        return;
      }

      const cached = readCPSDetail(currentProject.id, round.roundNumber);
      if (!cached) {
        prefetchingRef.current.add(key);

        fetch(`/api/marketer/cps-settlements/${currentProject.id}?roundNumber=${round.roundNumber}`, {
          credentials: 'include'
        })
          .then(res => res.json())
          .then(data => {
            if (data.ok) {
              cacheCPSDetail(currentProject.id, round.roundNumber, data);
            }
          })
          .catch(err => console.warn(`⚠️ [프리페치 실패] ${round.roundNumber}회차:`, err))
          .finally(() => {
            prefetchingRef.current.delete(key);
          });
      }
    };

    if (currentIndex < allSettlements.length - 1) {
      prefetchRound(allSettlements[currentIndex + 1]);
    }

    if (currentIndex > 0) {
      prefetchRound(allSettlements[currentIndex - 1]);
    }

    if (currentIndex < allSettlements.length - 2) {
      setTimeout(() => {
        prefetchRound(allSettlements[currentIndex + 2]);
      }, 100);
    }
  }, [selectedRound, currentProject, allSettlements]);

  const handleCustomerCountChange = useCallback((influencerId, value) => {
    const count = Math.max(0, parseInt(value) || 0);
    setCustomerCounts(prev => ({ ...prev, [influencerId]: count }));
  }, []);

  const handleSettleSuccess = useCallback(() => {
    setShowSettleModal(false);

    // 캐시 무효화
    if (currentProject && selectedRound) {
      invalidateCPSDetail(currentProject.id, selectedRound);
    }

    // 프로젝트 목록 및 상세 정보 새로고침
    if (currentProject) {
      fetchProjects(filter); // 프로젝트 목록 새로고침

      // 현재 선택된 회차 정보 새로고침 (forceRefresh=true로 캐시 무시하고 서버에서 최신 데이터 가져옴)
      setTimeout(() => {
        fetchSettlementDetail(currentProject.id, selectedRound, true, true);
      }, 300);
    }
  }, [currentProject, selectedRound, filter, fetchSettlementDetail, fetchProjects]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  const calculateDday = (dateStr) => {
    if (!dateStr) return '';
    const target = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);

    const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'D-Day';
    if (diff > 0) return `D-${diff}`;
    return `D+${Math.abs(diff)}`;
  };

  // ✅ D-day 배지 클래스 (렌더 전용: 기존 색 분기 → mb-badge 매핑)
  const getDdayBadgeClass = (dateStr) => {
    if (!dateStr) return 'mb-badge-gray';
    const target = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);

    const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
    if (diff <= 0) return 'mb-badge-red';
    if (diff <= 3) return 'mb-badge-amber';
    return 'mb-badge-green';
  };

  // ✅ 새로고침 함수
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchProjects(filter);
      if (currentProject?.id && currentProject?.currentRound) {
        await fetchSettlementDetail(currentProject.id, currentProject.currentRound, false, true);
      }
    } catch (e) {
      console.error('새로고침 오류:', e);
    } finally {
      setRefreshing(false);
    }
  }, [fetchProjects, filter, currentProject, fetchSettlementDetail]);

  // 렌더 전용: 서비스 방식 라벨
  const getServiceTypeLabel = (serviceType) => {
    if (serviceType === 'delivery') return '제품 제공';
    if (serviceType === 'purchase') return '제품 미제공';
    if (serviceType === 'visit') return '장소 방문';
    if (serviceType === 'free') return '자율';
    return '일반';
  };

  if (loading && !hasProjectCache) {
    return (
      <div className="mb-card" style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 15, color: '#6b7686' }}>프로젝트 목록을 불러오는 중...</div>
      </div>
    );
  }

  if (error && projects.length === 0) {
    return (
      <div className="mb-card" style={{ padding: 60, textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: '#e5484d' }}>{error}</div>
        <div style={{ fontSize: 14, color: '#6b7686' }}>프로젝트 목록을 불러오는 중 오류가 발생했습니다.</div>
      </div>
    );
  }

  return (
    <div>
      {/* 상단 안내 */}
      <div className="mb-page-header">
        <h2 className="mb-page-title">CPS 정산하기</h2>
        <p className="mb-page-description">
          구매당 리워드를 사용하는 프로젝트의 결산을 진행하고, 인플루언서에게 성과형 리워드를 지급하세요.
        </p>
      </div>

      {/* 필터 칩 */}
      <div className="chips">
        <button
          type="button"
          className={`chip ${filter === 'all' ? 'is-active' : ''}`}
          onClick={() => { if (filter !== 'all') handleFilterChange('all'); }}
        >
          전체 프로젝트
        </button>
        <button
          type="button"
          className={`chip ${filter === 'in_progress' ? 'is-active' : ''}`}
          onClick={() => { if (filter !== 'in_progress') handleFilterChange('in_progress'); }}
        >
          결산 기간 중인 프로젝트만
        </button>
      </div>

      {/* 프로젝트가 없을 때 빈 상태 표시 */}
      {projects.length === 0 ? (
        <div className="mb-card" style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: '#3b4658' }}>
            {filter === 'in_progress'
              ? '결산 기간 중인 프로젝트가 없습니다.'
              : '구매당 리워드를 사용하는 프로젝트가 없습니다.'
            }
          </div>
          <div style={{ fontSize: 14, color: '#6b7686' }}>
            {filter === 'in_progress'
              ? '필터를 해제하거나 결산일이 도래한 프로젝트를 확인해주세요.'
              : '프로젝트 생성 시 구매당 리워드(CPS)를 활성화해주세요.'
            }
          </div>
        </div>
      ) : (
        <>
          {/* 프로젝트 네비게이션 카드 */}
          <div className="pnav">
            <div className="pnav__top">
              <div className="pnav__pager">
                <button
                  type="button"
                  onClick={handlePrevProject}
                  disabled={currentProjectIndex === 0}
                  aria-label="이전 프로젝트"
                >
                  ‹
                </button>
                {currentProjectIndex + 1} / {projects.length}
                <button
                  type="button"
                  onClick={handleNextProject}
                  disabled={currentProjectIndex === projects.length - 1}
                  aria-label="다음 프로젝트"
                >
                  ›
                </button>
              </div>
              <div className="pnav__spacer" />
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                className="mb-btn mb-btn-secondary mb-btn-sm"
              >
                {refreshing ? '불러오는 중...' : '새로고침'}
              </button>
              <button
                type="button"
                onClick={() => setShowDetailModal(true)}
                className="mb-btn mb-btn-secondary mb-btn-sm"
              >
                프로젝트 상세보기
              </button>
            </div>

            {currentProject && (
              <>
                <div className="pnav__no">PROJECT NO.{currentProject.projectNumber || 'N/A'}</div>
                {currentProject.productName && (
                  <div className="pnav__title">
                    {currentProject.productName}을(를) 홍보하는 {getServiceTypeLabel(currentProject.serviceType)} 프로젝트입니다
                  </div>
                )}

                <div className="pnav__stats">
                  <div className="pnav__stat">
                    <b>{currentProject.completedRounds} / {currentProject.totalRounds}회</b>
                    <span>결산 회차 완료</span>
                  </div>
                  <div className="pnav__stat">
                    <b>{currentProject.contractedCount || 0}명</b>
                    <span>계약 인원</span>
                  </div>
                  <div className="pnav__stat">
                    <b>{currentProject.perPurchaseReward?.rewardPerBuyer?.toLocaleString() || 0}P</b>
                    <span>유입 구매고객 1명당 지급 포인트</span>
                  </div>
                </div>

                <div className="pnav__dates">
                  <span>
                    CPS 보증금(리워드 총 예산) <b>{currentProject.perPurchaseReward?.budgetTotal?.toLocaleString() || 0}P</b>
                  </span>
                  {currentProject.nextSettlementDate && (
                    <span>
                      다음 결산일 <b>{formatDate(currentProject.nextSettlementDate)}</b>{' '}
                      <span className={`mb-badge ${getDdayBadgeClass(currentProject.nextSettlementDate)}`}>
                        {calculateDday(currentProject.nextSettlementDate)}
                      </span>
                    </span>
                  )}
                </div>

                {currentProject.nextSettlementDate && (
                  <div className="note note--blue">
                    결산은 결산일로부터 7일 이내에 진행해주세요. 기간 내 미진행 시 셀프애드팀이 자동 결산합니다.
                  </div>
                )}
              </>
            )}
          </div>

          {/* 회차 선택 */}
          {allSettlements.length > 0 && (
            <div className="mb-card" style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 15.5, fontWeight: 800, color: '#0b1324', margin: '0 0 12px' }}>
                결산 회차
              </h3>
              {/* A안: 회차 타임라인 (클릭하면 해당 회차 상세로 전환) */}
              <div className="mbh-rounds" style={{ marginBottom: 8 }}>
                {allSettlements.map((settlement, i) => {
                  const done = settlement.status === 'completed' || settlement.status === 'rejected';
                  const periodStatus = getSettlementPeriodStatus(settlement.scheduledDate, settlement.status);
                  const statusLabel = done
                    ? (settlement.isAutoSettled ? '자동 결산 완료' : '결산 완료')
                    : settlement.status === 'auto_reserved' ? '결산 미진행'
                    : periodStatus === 'active' ? '지금이 결산 기간이에요'
                    : periodStatus === 'expired' ? '결산 기한 경과'
                    : '결산 예정';
                  const badgeClass = done ? 'badge--mint'
                    : periodStatus === 'active' ? 'badge--blue'
                    : periodStatus === 'expired' ? 'badge--red'
                    : 'badge--gray';
                  return (
                    <button
                      key={settlement.roundNumber}
                      type="button"
                      onClick={() => handleRoundChange(settlement.roundNumber)}
                      className={`mbh-round ${done ? 'is-done' : ''} ${selectedRound === settlement.roundNumber ? 'is-active' : ''}`}
                    >
                      <span className="mbh-round__rail">
                        <span className="mbh-round__dot">{done ? '✓' : settlement.roundNumber}</span>
                        {i < allSettlements.length - 1 && <span className="mbh-round__line" />}
                      </span>
                      <span className="mbh-round__body">
                        <span className="mbh-round__title">
                          {settlement.roundNumber}회차 정산
                          <span className={`badge ${badgeClass}`}>{statusLabel}</span>
                        </span>
                        <span className="mbh-round__sub">결산일 {formatDate(settlement.scheduledDate)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* 현재 회차 정보 */}
              {currentSettlement && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                  background: '#f6f8fb',
                  borderRadius: 14,
                  padding: '12px 16px'
                }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#3b4658' }}>
                    결산일 <b style={{ fontVariantNumeric: 'tabular-nums', color: '#0b1324' }}>{formatDate(currentSettlement.scheduledDate)}</b>
                    {' · '}
                    {(() => {
                      const periodStatus = getSettlementPeriodStatus(currentSettlement.scheduledDate, currentSettlement.status);

                      if (currentSettlement.isAutoSettled && (currentSettlement.status === 'completed' || currentSettlement.status === 'rejected')) {
                        return <span style={{ color: '#2f6bff', fontWeight: 700 }}>셀프애드팀 직접 결산 완료</span>;
                      }

                      if (currentSettlement.status === 'completed') {
                        return <span style={{ color: '#0fb37a', fontWeight: 700 }}>결산 완료</span>;
                      }

                      if (periodStatus === 'auto_reserved' || currentSettlement.status === 'auto_reserved') {
                        return <span style={{ color: '#9a6b00', fontWeight: 700 }}>결산 미진행</span>;
                      }

                      if (periodStatus === 'before') {
                        return <span style={{ color: '#6b7686', fontWeight: 700 }}>결산 예정</span>;
                      }

                      if (periodStatus === 'active') {
                        return <span style={{ color: '#0fb37a', fontWeight: 700 }}>결산 가능</span>;
                      }

                      if (periodStatus === 'expired') {
                        return <span style={{ color: '#e5484d', fontWeight: 700 }}>결산 기한 경과</span>;
                      }

                      return <span style={{ color: '#6b7686', fontWeight: 700 }}>대기 중</span>;
                    })()}
                  </span>
                  {currentSettlement.status !== 'completed' && currentSettlement.status !== 'rejected' && currentSettlement.scheduledDate && (
                    <span className={`mb-badge ${getDdayBadgeClass(currentSettlement.scheduledDate)}`}>
                      {calculateDday(currentSettlement.scheduledDate)}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 결산 테이블 */}
          {settlementLoading ? (
            <div className="mb-card" style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 15, color: '#6b7686' }}>결산 데이터를 불러오는 중...</div>
            </div>
          ) : !currentSettlement ? (
            <div className="mb-card" style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 15, color: '#6b7686' }}>결산 데이터가 없습니다.</div>
            </div>
          ) : currentSettlement.status === 'completed' || currentSettlement.status === 'rejected' ? (
            <div>
              {/* ✅ 자동 결산 완료 */}
              {currentSettlement.isAutoSettled ? (
                <div className="mb-card" style={{ textAlign: 'center', padding: 32 }}>
                  <div style={{ color: '#2f6bff', fontWeight: 800, fontSize: 16, marginBottom: 12 }}>
                    셀프애드팀 직접 결산 완료
                  </div>
                  <div style={{ color: '#6b7686', fontSize: 14, lineHeight: 1.6 }}>
                    결산 기간 동안 결산을 진행하지 않아, 셀프애드팀이 직접 결산을 완료하였습니다.
                  </div>
                </div>
              ) : (
                /* ✅ 광고주 직접 결산 */
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    padding: 32,
                    background: '#e7f7f0',
                    borderRadius: 'var(--mb-card-radius)',
                    marginBottom: 24
                  }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#0fb37a', marginBottom: 12 }}>
                      {selectedRound}회차 구매당 리워드 결산 완료
                    </div>
                    {currentSettlement.completedAt && (
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0fb37a', marginBottom: 8 }}>
                        결산 완료일: {formatDate(currentSettlement.completedAt)}
                      </div>
                    )}
                    {selectedRound < allSettlements.length && allSettlements[selectedRound] && (
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0fb37a' }}>
                        다음 결산 예정일: {formatDate(allSettlements[selectedRound].scheduledDate)}
                      </div>
                    )}
                    {selectedRound === allSettlements.length && (
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0fb37a' }}>
                        모든 결산이 완료되었습니다. 프로젝트가 곧 완료됩니다.
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowHistoryModal(true)}
                    className="mb-btn mb-btn-primary mb-btn-lg"
                  >
                    결산 내역 보기
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="board">
                <div className="board__head" style={{ paddingBottom: 12 }}>
                  <h3>인플루언서 결산 목록 ({currentSettlement.settlementData?.length || 0}명)</h3>
                </div>

                <div className="mb-table-container" style={{ boxShadow: 'none', borderRadius: 0, maxHeight: 600, overflowY: 'auto' }}>
                  <table className="mb-table">
                    <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                      <tr>
                        <th style={{ minWidth: 50 }}>NO</th>
                        <th style={{ minWidth: 120 }}>인플루언서</th>
                        <th style={{ minWidth: 140 }}>유입 고객 수</th>
                        <th style={{ minWidth: 120 }}>지급 포인트</th>
                        <th style={{ minWidth: 120 }}>최대 지급 가능</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(currentSettlement.settlementData || []).map((data, idx) => (
                        <tr key={data.influencerId}>
                          <td data-label="NO" style={{ color: '#6b7686', fontSize: 14 }}>{idx + 1}</td>
                          <td data-label="인플루언서">
                            <div className="t-name">{data.userNickname || data.username}</div>
                            {data.profileImg && (
                              <img
                                src={data.profileImg}
                                alt="프로필"
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: '1px solid #eceef2',
                                  marginTop: 6
                                }}
                              />
                            )}
                            {data.trackingUrl && (
                              <div className="t-sub" style={{ marginTop: 6, fontSize: 11, lineHeight: 1.4, wordBreak: 'break-all' }}>
                                CPS 식별 URL: {data.trackingUrl}
                              </div>
                            )}
                            {(data.phoneNumber || data.userAddress) && (
                              <div className="t-sub" style={{ marginTop: 6, fontSize: 11, lineHeight: 1.4 }}>
                                {data.phoneNumber && (
                                  <div style={{ marginBottom: data.userAddress ? 2 : 0 }}>연락처 {data.phoneNumber}</div>
                                )}
                                {data.userAddress && (
                                  <div>주소 {data.userAddress}</div>
                                )}
                              </div>
                            )}
                          </td>
                          <td data-label="유입 고객 수">
                            {data.customerCount > 0 ? (
                              <span className="t-num" style={{ fontSize: 15 }}>{data.customerCount}명</span>
                            ) : (
                              <div>
                                <input
                                  type="number"
                                  min="0"
                                  value={customerCounts[data.influencerId] || ''}
                                  onChange={(e) => handleCustomerCountChange(data.influencerId, e.target.value)}
                                  placeholder="0"
                                  className="cnt-input"
                                />
                                {' '}
                                <span className="t-sub" style={{ display: 'inline' }}>명</span>
                                {(() => {
                                  const inputCount = customerCounts[data.influencerId] || 0;
                                  const maxCustomers = Math.floor(data.maxRewardAmount / (currentProject.perPurchaseReward?.rewardPerBuyer || 1));

                                  if (inputCount > maxCustomers) {
                                    return (
                                      <div style={{
                                        marginTop: 6,
                                        padding: '6px 10px',
                                        background: '#fff3d6',
                                        borderRadius: 8,
                                        fontSize: 12,
                                        color: '#9a6b00',
                                        fontWeight: 600,
                                        lineHeight: 1.4
                                      }}>
                                        최대 {maxCustomers}명까지 지급 가능합니다.
                                        <br />
                                        초과 입력하셔도 최대 금액까지만 지급됩니다.
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            )}
                          </td>
                          <td data-label="지급 포인트" className="t-num" style={{ fontSize: 15 }}>
                            {data.rewardAmount > 0
                              ? `${data.rewardAmount.toLocaleString()}P`
                              : (() => {
                                  const count = customerCounts[data.influencerId] || 0;
                                  const rewardPerBuyer = currentProject.perPurchaseReward?.rewardPerBuyer || 0;
                                  const calculatedReward = count * rewardPerBuyer;
                                  const actualReward = Math.min(calculatedReward, data.maxRewardAmount);
                                  return `${actualReward.toLocaleString()}P`;
                                })()
                            }
                          </td>
                          <td data-label="최대 지급 가능" className="t-num" style={{ color: '#6b7686', fontSize: 14 }}>
                            {data.maxRewardAmount.toLocaleString()}P
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 결산하기 버튼 */}
              <div style={{ marginTop: 16 }}>
                {(() => {
                  const periodStatus = getSettlementPeriodStatus(
                    currentSettlement.scheduledDate,
                    currentSettlement.status
                  );

                  if (currentSettlement.isAutoSettled && (currentSettlement.status === 'completed' || currentSettlement.status === 'rejected')) {
                    return (
                      <div className="note note--blue">
                        결산 기간 동안 결산을 진행하지 않아, 셀프애드팀이 직접 결산 완료하였습니다.
                      </div>
                    );
                  }

                  if (periodStatus === 'before') {
                    return (
                      <div className="note note--amber">
                        결산일이 도래하면 결산하기 버튼이 활성화됩니다.
                      </div>
                    );
                  }

                  if (periodStatus === 'expired' || periodStatus === 'auto_reserved') {
                    return (
                      <div className="note note--amber">
                        <b>셀프애드팀 직접 결산 진행 중</b>
                        <br />
                        결산 기간이 종료되어 관리자가 직접 결산을 진행하고 있습니다.
                      </div>
                    );
                  }

                  return (
                    <button
                      type="button"
                      onClick={() => setShowSettleModal(true)}
                      disabled={currentSettlement.status !== 'ready'}
                      className="mb-btn mb-btn-primary mb-btn-lg mb-btn-full"
                    >
                      유입 고객 수 입력하고 {selectedRound}회차 정산 완료하기
                    </button>
                  );
                })()}
              </div>
            </div>
          )}
        </>
      )}

      {/* 모달들 */}
      {showDetailModal && currentProject && (
        <ProjectDetailModal
          open={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          project={currentProject}
        />
      )}

      {showSettleModal && currentProject && (
        <SettleModal
          open={showSettleModal}
          onClose={() => setShowSettleModal(false)}
          settlement={currentSettlement}
          project={currentProject}
          customerCounts={customerCounts}
          onSuccess={handleSettleSuccess}
        />
      )}

      {showHistoryModal && currentProject && (
        <SettlementHistoryModal
          open={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          settlement={currentSettlement}
          project={currentProject}
        />
      )}
    </div>
  );
}

export default MarketerProjectCPSCharge;
